import React, { useEffect, useState } from 'react';
import { useSocial } from '../SocialContext';
import { feedService } from '../../../core/services/firebase/feedService';
import { socialService } from '../../../core/services/firebase/socialService';
import { SocialPost, SocialProfile } from '../../../core/types/social';
import { PostComposer } from '../components/PostComposer';
import { PostCard } from '../components/PostCard';
import { UserSearchBar } from '../../../components/UserSearchBar';
import { NotificationBell } from '../../../components/NotificationBell';
import { Loader2, Fish, Users, Globe, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { postService } from '../../../core/services/firebase/postService';

type FeedType = 'personalized' | 'global';

export const FeedPage: React.FC = () => {
  const { user } = useAuth();
  const { profile } = useSocial();
  const [feedType, setFeedType] = useState<FeedType>('personalized');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [authorProfiles, setAuthorProfiles] = useState<Record<string, SocialProfile>>({});

  const loadFeed = async (isInitial: boolean = true) => {
    if (isInitial) {
      setLoading(true);
      setPosts([]);
      setLastVisible(null);
      setHasMore(true);
    } else if (!hasMore || loadingMore) {
      return;
    } else {
      setLoadingMore(true);
    }

    try {
      let result: { posts: SocialPost[], lastDoc: any };
      
      if (selectedTag) {
        const tagPosts = await postService.getPostsByTag(selectedTag, 30);
        result = { posts: tagPosts, lastDoc: null };
      } else if (feedType === 'personalized' && profile) {
        result = await feedService.getPersonalizedFeed(profile.handle, 20, isInitial ? undefined : lastVisible);
      } else {
        result = await feedService.getGlobalFeed(20, isInitial ? undefined : lastVisible);
      }
      
      if (isInitial) {
        setPosts(result.posts);
      } else {
        setPosts(prev => [...prev, ...result.posts]);
      }

      setLastVisible(result.lastDoc);
      setHasMore(result.lastDoc !== null);

      const uniqueAuthors = [...new Set(result.posts.map(p => p.authorId))];
      const profilesData: Record<string, SocialProfile> = { ...authorProfiles };
      
      await Promise.all(
        uniqueAuthors.map(async (authorId) => {
          if (!profilesData[authorId]) {
            const authorProfile = await socialService.getProfileByHandle(authorId);
            if (authorProfile) {
              profilesData[authorId] = authorProfile;
            }
          }
        })
      );
      
      setAuthorProfiles(profilesData);
    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
       setLoading(false);
       setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFeed(true);
  }, [feedType, profile, selectedTag]);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadFeed(false);
        }
      },
      { threshold: 0.1 }
    );

    const target = document.querySelector('#feed-end-observer');
    if (target) observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, lastVisible]);

  // Extract popular tags from posts (for the sidebar)
  const popularTags = Array.from(new Set(posts.flatMap(p => p.tags || []))).slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-1.5 rounded-lg shadow-blue-200 shadow-md">
              <Fish className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 tracking-tight">COMUNIDADE</h1>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest -mt-1">Nossa Pesca 3.0</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 flex-1 max-w-md mx-8">
            <UserSearchBar placeholder="Buscar pescadores ou técnicas..." />
          </div>

          <div className="flex items-center gap-4">
            {user && profile && (
              <NotificationBell userId={user.uid} userHandle={profile.handle} />
            )}
            {profile && (
              <a href={`/${profile.handle}`} className="w-9 h-9 rounded-full overflow-hidden border-2 border-white shadow-sm hover:border-blue-500 transition-all">
                {profile.avatarUrl ? (
                   <img src={profile.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-600 font-black">
                    {profile.displayName[0]}
                  </div>
                )}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Sidebar - Navigation & Filters */}
        <aside className="lg:col-span-3 space-y-6 hidden lg:block">
          <nav className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-1">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest px-3 mb-4">Navegação</h3>
            <button
              onClick={() => { setFeedType('personalized'); setSelectedTag(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-bold transition-all ${
                feedType === 'personalized' && !selectedTag ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Users size={20} />
              Para Você
            </button>
            <button
              onClick={() => { setFeedType('global'); setSelectedTag(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-bold transition-all ${
                feedType === 'global' && !selectedTag ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Globe size={20} />
              Explorar Global
            </button>
          </nav>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest px-3 mb-4">Tendências #</h3>
            <div className="flex flex-wrap gap-2 px-2">
              {['tucunaré', 'robalo', 'fly', 'pescaesportiva', 'equipamentos', 'iscas'].map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-black transition-all ${
                    selectedTag === tag 
                      ? 'bg-blue-600 text-white shadow-lg' 
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl">
             <h4 className="font-black text-lg mb-2">Memória Viva 🎣</h4>
             <p className="text-xs text-blue-100 mb-4 opacity-80">Revisite suas melhores capturas navegando pelo tempo.</p>
             <button className="w-full bg-white/20 hover:bg-white/30 py-2 rounded-lg text-xs font-bold transition-colors">
                Ver Minha Timeline
             </button>
          </div>
        </aside>

        {/* Center - Feed Content */}
        <section className="lg:col-span-6 space-y-6">
          {/* Active Filter Indicator */}
          {selectedTag && (
            <div className="bg-blue-600 text-white px-4 py-2 rounded-xl flex items-center justify-between shadow-lg">
              <span className="font-black">Mostrando posts sobre #{selectedTag}</span>
              <button 
                onClick={() => setSelectedTag(null)}
                aria-label="Limpar filtro"
                className="bg-white/20 p-1 rounded-full hover:bg-white/30"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* Post Composer */}
          {profile && <PostComposer onPostCreated={loadFeed} />}

          {/* Feed List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
              <div className="text-6xl mb-6">🏜️</div>
              <h3 className="text-xl font-black text-gray-900 mb-2">Nada por aqui hoje</h3>
              <p className="text-gray-500 text-sm mb-6">
                Tente mudar o filtro ou seguir novos pescadores!
              </p>
              <button
                onClick={() => { setFeedType('global'); setSelectedTag(null); }}
                className="bg-blue-600 text-white px-8 py-3 rounded-full font-black hover:bg-blue-700 transition-all shadow-lg"
              >
                Explorar Feed Global
              </button>
            </div>
          ) : (
            <div className="space-y-6 pb-20">
              {posts.map((post) => (
                <PostCard 
                  key={post.id} 
                  post={post}
                  authorName={authorProfiles[post.authorId]?.displayName}
                  authorAvatar={authorProfiles[post.authorId]?.avatarUrl}
                  authorVerification={authorProfiles[post.authorId]?.verification}
                  onUpdate={loadFeed}
                />
              ))}
              
              {/* Observer Target */}
              <div id="feed-end-observer" className="h-10" />

              {loadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="animate-spin text-blue-600" size={24} />
                </div>
              )}

              {!hasMore && posts.length > 0 && (
                <div className="text-center py-10 text-gray-400 text-sm font-bold uppercase tracking-widest">
                  Você chegou ao fim do cardume 🐟
                </div>
              )}
            </div>
          )}
        </section>

        {/* Right Sidebar - Suggestions & Stats */}
        <aside className="lg:col-span-3 space-y-6 hidden lg:block">
           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Quem seguir</h3>
              <div className="space-y-4">
                 <p className="text-xs text-gray-400 text-center py-4">Sugestões baseadas nas suas pescarias em breve...</p>
              </div>
           </div>

           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Sua Reputação</h3>
              <div className="flex items-center gap-4">
                 <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full w-[15%] shadow-[0_0_8px_rgba(37,99,235,0.4)]"></div>
                 </div>
                 <span className="text-sm font-black text-blue-600">150 XP</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase">Nível 1: Pescador de Barranco</p>
           </div>
        </aside>
      </main>
    </div>
  );
};
